import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from './Button';

interface ConfirmDeleteModalProps {
	isOpen: boolean;
	title: string;
	message: string;
	onConfirm: () => void;
	onCancel: () => void;
	isLoading?: boolean;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
	isOpen,
	title,
	message,
	onConfirm,
	onCancel,
	isLoading = false
}) => {
	const { t } = useTranslation();

	// Animation variants
	const overlayVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 }
	};

	const modalVariants = {
		hidden: { opacity: 0, scale: 0.9, y: 20 },
		visible: {
			opacity: 1,
			scale: 1,
			y: 0,
			transition: {
				type: "spring" as const,
				stiffness: 300,
				damping: 30
			}
		}
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
						{/* Background overlay */}
						<motion.div
							className="fixed inset-0 bg-black/50"
							variants={overlayVariants}
							initial="hidden"
							animate="visible"
							exit="hidden"
							onClick={onCancel}
						/>

						{/* Modal panel */}
						<motion.div
							className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white dark:bg-gray-800 rounded-lg shadow-xl relative z-10"
							variants={modalVariants}
							initial="hidden"
							animate="visible"
							exit="hidden"
						>
							<div className="flex items-start">
								<div className="flex-shrink-0 flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full">
									<FiAlertTriangle className="text-red-600 dark:text-red-400 w-6 h-6" />
								</div>

								<div className="ml-4 flex-1">
									<h3 className="text-lg font-medium text-gray-900 dark:text-white">
										{title}
									</h3>
									<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
										{message}
									</p>
								</div>
							</div>

							<div className="mt-6 flex justify-end space-x-3">
								<Button
									variant="outline"
									className="!text-red-600 hover:!bg-red-50 dark:hover:!bg-red-900/20"
									onClick={onConfirm}
									isLoading={isLoading}
								>
									{t('common.delete')}
								</Button>
								<Button
									variant="outline"
									onClick={onCancel}
									disabled={isLoading}
								>
									{t('common.cancel')}
								</Button>
							</div>
						</motion.div>
					</div>
				</div>
			)}
		</AnimatePresence>
	);
};

export default ConfirmDeleteModal;